<script lang="ts">
  /**
   * ClubSwimDetail — the unified swim card (B-026, build 13). Tapping any bar in the Club
   * strip opens exactly this, and nothing else on the Club tab compares swims (B-027).
   * Top to bottom, one scroll:
   *
   *   1. HEADER     name or "You", the day on its own line, close.
   *   2. STATS      distance · duration · pool, per swim (a bar is a DAY: usually one swim).
   *   3. SETS       the Swim tab's SetRibbon at full size. Your own sets keep their tap (it
   *                 opens the swim on the Swim tab, where SetDetailSheet lives); a clubmate's
   *                 are a picture, hidden from VoiceOver, because SetDetailSheet writes through
   *                 the Swim tab's selected swim. No lengths: "Watch logged 53 min, no lengths".
   *                 Your own open-water swim shows its route here instead.
   *   4. COMPARE    automatic, from swimCard.compareZone: one row per other swimmer that day
   *                 (name · distance · time in words, one capped SetGroupRibbon line, the
   *                 stroke caption, a chevron), then "+N more swam today", then reason lines.
   *                 Read-only apart from the row tap, which opens that swimmer's card. Never
   *                 a horizontal scroll, no deltas, no rank, no pace shading.
   *   5. REACTIONS  one row of the six preset emoji with counts, per swim. No name list, no
   *                 caption: reactions are permanently minor (decision 4).
   *
   * Two sizes of one grammar (decision 3): big labeled chips for the swim you opened,
   * condensed capped chips for who else swam that day.
   */
  import type { ClubMember, ClubReaction, ClubSwim, PresetEmoji } from "../lib/club/snapshot";
  import { PRESET_EMOJI } from "../lib/club/snapshot";
  import { reactionsForSwim, receivedCounts, receivedLabel } from "../lib/club/strip";
  import { NOT_ON_PHONE_LINE, noLengthsLine, rowSetGroup, setsFooter, shareKind, shareLine, sourceKind, sourceLine, type CompareRow, type CompareZone, type SwimSets } from "../lib/club/swimCard";
  import { ownSetTap } from "../lib/club/ownSetTap";
  import { rowMaxChips } from "../lib/setPage";
  import { cardDayText, fmtMeters, fmtTimeOfDay, formatDuration } from "../lib/format";
  import { haptic } from "../lib/haptics";
  import SetRibbon from "./SetRibbon.svelte";
  import SetGroupRibbon from "./SetGroupRibbon.svelte";
  import OpenWaterRouteCard from "./OpenWaterRouteCard.svelte";

  interface Props {
    member: ClubMember;
    /** Every swim on the tapped day, newest first. Usually one. */
    swims: ClubSwim[];
    isSelf: boolean;
    selfId: string;
    now: number;
    /** Where each swim's sets come from (swimCard.setsForSwim, bound by the tab). */
    setsFor: (member: ClubMember, swim: ClubSwim) => SwimSets;
    /** The compare zone for this day; null renders nothing (nobody else swam). */
    compare?: CompareZone | null;
    /** A compare row tap: open that swimmer's card for the same day. */
    onOpenSwimmer?: (member: ClubMember, swims: ClubSwim[]) => void;
    /** Own swims the club never received (older than the snapshot, B-028): no reactions row. */
    sessionOnly?: ReadonlySet<string>;
    /** Your own open-water swim's workout uuid (its route replaces the ribbon), else null. */
    routeFor?: (swim: ClubSwim) => string | null;
    /** Fresh (24 h) reactions, every swim; this card picks its own. */
    reactions?: readonly ClubReaction[];
    onReact?: (swimId: string, emoji: PresetEmoji | null) => void;
    /** The last tap did not reach the club; say so, keep the picker live. */
    reactFailed?: boolean;
    /** The date row above shows its short label, so the date line does too (B-031). */
    shortDate?: boolean;
    onClose: () => void;
  }
  let {
    member,
    swims,
    isSelf,
    selfId,
    now,
    setsFor,
    compare = null,
    onOpenSwimmer,
    sessionOnly = new Set<string>(),
    routeFor,
    reactions = [],
    onReact,
    reactFailed = false,
    shortDate = false,
    onClose,
  }: Props = $props();

  let who = $derived(isSelf ? "You" : member.name);
  let first = $derived(swims[0]);
  let when = $derived(first ? cardDayText(first.date, new Date(now), shortDate) : "");
  let ids = $derived(swims.map((s) => s.id));

  function summaryOf(swim: ClubSwim) {
    return reactionsForSwim(reactions, swim.id, selfId);
  }

  function tap(swim: ClubSwim, emoji: PresetEmoji): void {
    haptic("light");
    // Tapping your own reaction again removes it (C5); any other emoji replaces it.
    onReact?.(swim.id, summaryOf(swim).mine === emoji ? null : emoji);
  }

  let reactable = $derived(swims.filter((s) => !sessionOnly.has(s.id)));

  function openRow(row: CompareRow): void {
    haptic("selection");
    onOpenSwimmer?.(row.member, row.swims);
  }
</script>

<div
  class="detail"
  data-testid="club-swim-detail"
  data-swim-id={first?.id}
  data-swim-ids={ids.join(",")}
  data-member-id={member.id}
  role="region"
  aria-label={`${who}, ${when}${swims.length > 1 ? `, ${swims.length} swims` : ""}`}
>
  <!-- Name and the close glyph share a line; the date gets its own, never wrapped
       (VP round 2 item 4: "Done" squeezed "Wednesday, / Sep 9" onto two lines). -->
  <div class="head">
    <div class="head-text">
      <p class="who">{who}{#if swims.length > 1}<span class="when">{` · ${swims.length} swims`}</span>{/if}</p>
      <p class="when-line">{when}</p>
    </div>
    <button class="close" type="button" onclick={onClose} aria-label="Close">
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  </div>

  {#each swims as swim, i (swim.id)}
    {@const sets = setsFor(member, swim)}
    {@const line = sourceLine(sets)}
    {@const route = routeFor?.(swim) ?? null}
    <div class="swim" data-testid="club-swim-block" data-swim-id={swim.id} class:divided={i > 0}>
      <p class="nums">
        {#if swims.length > 1}<span class="tod">{fmtTimeOfDay(Date.parse(swim.date))} · </span>{/if}
        {fmtMeters(swim.distanceM)} · {formatDuration(swim.durationSec)}{(swim.poolM ?? 0) > 0 ? ` · ${fmtMeters(swim.poolM)} pool` : ""}
      </p>

      {#if route}
        <OpenWaterRouteCard workoutUUID={route} openWater={true} />
      {:else if sets.groups.length > 0}
        <div
          class="sets"
          class:estimate={sets.source === "summary"}
          data-testid="club-sets"
          data-source={sets.source}
          aria-hidden={isSelf ? undefined : "true"}
          data-tappable={isSelf ? "true" : "false"}
        >
          <SetRibbon groups={sets.groups} interactive={isSelf} onSetTap={() => ownSetTap(isSelf, swim)} />
        </div>
        <p class="sets-total">{setsFooter(sets.groups)}</p>
        {#if line}<p class="sets-total status {sourceKind(sets)}" data-testid="club-sets-source">{line}</p>{/if}
      {:else if sets.source === "none"}
        <!-- An own swim older than the snapshot with no firehose on this phone: its sets exist
             elsewhere, so never "no lengths" (B-028). -->
        <p class="sets-total" data-testid="club-sets-elsewhere">{NOT_ON_PHONE_LINE}</p>
      {:else if line}
        <!-- Build 16: a failed or pending fetch says so; C5 never stands in silently. -->
        <p class="sets-total status {sourceKind(sets)}" data-testid="club-sets-source">{line}</p>
      {:else}
        <!-- Native sends no sets exactly when the watch recorded no lengths (B-019):
             say what the watch did log, and nothing pretends to be a set. -->
        <p class="sets-total" data-testid="club-no-lengths">{noLengthsLine(swim)}</p>
      {/if}
      {#if isSelf && shareLine(sets)}<p class="sets-total status {shareKind(sets)}" data-testid="club-sets-share">{shareLine(sets)}</p>{/if}
    </div>
  {/each}

  {#if compare}
    <div class="compare" data-testid="club-compare">
      {#each compare.rows as row (row.member.id)}
        <button
          type="button"
          class="row"
          data-testid="club-compare-row"
          data-member-id={row.member.id}
          aria-label={`${row.line}. ${row.caption}. Opens ${row.isSelf ? "your" : `${row.name}'s`} swim.`}
          onclick={() => openRow(row)}
        >
          <span class="row-main">
            <span class="row-line">{row.line}</span>
            <SetGroupRibbon setGroup={rowSetGroup(row.laps)} maxChips={rowMaxChips(row.laps.length)} />
            <span class="row-caption">{row.caption}</span>
          </span>
          <span class="chev" aria-hidden="true">›</span>
        </button>
      {/each}
      {#if compare.overflowLine}
        <p class="overflow" data-testid="club-compare-more">{compare.overflowLine}</p>
      {/if}
      {#each compare.reasons as reason (reason.member.id)}
        {#if reason.kind === "unsupported"}
          <!-- Newer-than-app clubmate: the visible rejection, never a silent gap (Phase 5). -->
          <div class="reject" data-testid="club-compare-reject">
            <p class="reject-title">Can't compare with {reason.name}</p>
            <p class="reject-body">Their swim was recorded by a newer version of Swim Set Club than this build supports. Open TestFlight and update Swim Set Club to see it here.</p>
          </div>
        {:else}
          <p class="reason" data-testid="club-compare-reason">{reason.text}</p>
        {/if}
      {/each}
    </div>
  {/if}

  {#each reactable as swim (swim.id)}
    {#if isSelf}
      <!-- Build 15b: no picker on your own swim, only what the club gave it (nothing when none did). -->
      {@const received = receivedCounts(reactions, swim.id, selfId)}
      {#if received.length > 0}
        <p class="received" data-testid="club-react-counts" data-swim-id={swim.id} aria-label={`${receivedLabel(received)}${swims.length > 1 ? `, ${fmtTimeOfDay(Date.parse(swim.date))} swim` : ""}`}>
          {#each received as c (c.emoji)}<span class="count">{c.emoji}<span class="n">{c.count}</span></span>{/each}
        </p>
      {/if}
    {:else}
    {@const summary = summaryOf(swim)}
    <div class="react" data-testid="club-react-picker" data-swim-id={swim.id} role="group" aria-label={`React to ${member.name}'s swim${swims.length > 1 ? ` at ${fmtTimeOfDay(Date.parse(swim.date))}` : ""}`}>
      {#each PRESET_EMOJI as emoji (emoji)}
        {@const n = summary.counts.find((c) => c.emoji === emoji)?.count ?? 0}
        <button
          type="button"
          class="emoji"
          class:mine={summary.mine === emoji}
          data-emoji={emoji}
          aria-pressed={summary.mine === emoji}
          aria-label={`${emoji}${n > 0 ? `, ${n}` : ""}${summary.mine === emoji ? ", yours, tap to remove" : ""}`}
          onclick={() => tap(swim, emoji)}
        >
          <span class="glyph">{emoji}</span>
          {#if n > 0}<span class="n">{n}</span>{/if}
        </button>
      {/each}
    </div>
    {/if}
  {/each}
  {#if reactFailed}
    <p class="react-fail" role="status">Couldn't send that to the club. Check your connection and tap again.</p>
  {/if}
</div>

<style>
  /* Your own swim: counts as plain pills, no border, so nothing reads as a button (build 15b). */
  .received {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
  }
  .count {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 28px;
    padding: 0 10px;
    border-radius: var(--radius-pill);
    background: var(--bg-raised);
    font-size: 16px;
    line-height: 1;
  }
  .detail {
    background: var(--bg-card);
    border: 1px solid var(--accent-primary);
    border-radius: var(--radius-card);
    padding: 12px var(--space-4) var(--space-3);
    display: flex;
    flex-direction: column;
    gap: 10px;
    /* A row tap scrolls the new card up under the sticky date row. */
    scroll-margin-top: calc(env(safe-area-inset-top, 0px) + 64px);
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
  }
  .head-text {
    min-width: 0;
    flex: 1 1 auto;
  }
  .who {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }
  .when {
    font-weight: 400;
    color: var(--text-muted);
  }
  .when-line {
    font-size: 15px;
    color: var(--text-muted);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .nums {
    font-size: 14px;
    color: var(--text-body);
    margin: 0;
  }
  .tod {
    color: var(--text-muted);
  }
  .swim {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .swim.divided {
    border-top: 1px solid var(--divider);
    padding-top: 10px;
  }
  .close {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-pill);
    color: var(--text-primary);
    padding: 0;
    cursor: pointer;
  }
  /* SetRibbon is a <section class="ribbon">, and app.css frames every `#app-root section[class]`
     as a glass card (an ID rule, so scoped styles lose without !important). Inside this card
     that drew a box in a box; the ribbon sits on the card's own ground. */
  .sets :global(section.ribbon) {
    border-color: transparent !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
  /* VP round 1: the roster's status cues, same meanings. */
  .sets.estimate {
    opacity: 0.55;
  }
  .status::before {
    display: inline-block;
    width: 1.1em;
    font-weight: 700;
  }
  .status.estimate {
    color: var(--accent-secondary);
    font-weight: 600;
  }
  .status.estimate::before {
    content: "≈" / "";
  }
  .status.pending::before {
    content: "↻" / "";
  }
  .status.error {
    color: var(--accent-error);
  }
  .status.error::before {
    content: "!" / "";
  }
  .sets-total {
    font: var(--type-caption);
    color: var(--text-muted);
    margin: 0;
  }

  /* Compare: stacked fixed-width rows, never a horizontal scroll. */
  .compare {
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--divider);
    min-width: 0;
  }
  .row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-width: 0;
    min-height: 56px;
    padding: 10px 0;
    background: transparent;
    border: none;
    text-align: left;
    font-family: var(--font-text);
    color: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  /* One hairline BETWEEN rows; the zone's top line and the reactions' top line bound it. */
  .row + .row {
    border-top: 1px solid var(--divider);
  }
  .row:active {
    background: var(--bg-raised);
  }
  .row-main {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }
  .row-line {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .row-caption {
    font-size: 12px;
    letter-spacing: 0.02em;
    color: var(--text-muted);
  }
  .chev {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 1;
    color: var(--text-dim);
  }
  .overflow,
  .reason {
    font-size: 14px;
    color: var(--text-muted);
    margin: 0;
    padding: 10px 0 0;
  }
  .reject {
    margin-top: 10px;
    border: 1px solid var(--accent-fire);
    border-radius: var(--radius-card);
    padding: 10px var(--space-3);
  }
  .reject-title {
    font: var(--type-body);
    color: var(--accent-fire);
    margin: 0 0 4px;
    font-weight: 700;
  }
  .reject-body {
    font: var(--type-body-sm);
    color: var(--text-muted);
    margin: 0;
  }

  /* Six emoji, one row, 44 px tap targets. */
  .react {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 6px;
    border-top: 1px solid var(--divider);
    padding-top: 10px;
  }
  .emoji {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 44px;
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    font-family: var(--font-text);
    cursor: pointer;
    padding: 0 4px;
    color: var(--text-primary);
  }
  .emoji.mine {
    border-color: var(--accent-primary);
    background: var(--bg-highest);
  }
  .glyph {
    font-size: 20px;
    line-height: 1;
  }
  .n {
    font: var(--type-caption);
    color: var(--text-muted);
  }
  .emoji.mine .n {
    color: var(--text-primary);
  }
  .react-fail {
    font-size: 13px;
    color: var(--accent-error);
    margin: 0;
  }
</style>
